import React, { useState, useEffect } from 'react';
import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    InformationCircleIcon,
    DocumentTextIcon,
    ShieldCheckIcon,
    HomeIcon,
    ClipboardDocumentCheckIcon,
    DocumentDuplicateIcon,
    CurrencyDollarIcon,
    MapPinIcon,
    PencilIcon,
    EyeIcon,
    PlusIcon,
    BuildingOfficeIcon,
} from "@heroicons/react/24/outline";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const axiosTimeoutConfig = { timeout: 30000 };

const CheckEligibility = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        gender: '',
        insuranceIdNumber: '',
        sequenceNumber: '',
        consentGiven: false,
    });

    const [departments, setDepartments] = useState([]);
    const [userDepartment, setUserDepartment] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [isChangingDepartment, setIsChangingDepartment] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isInsuranceUploading, setIsInsuranceUploading] = useState(false);
    const [isGettingPackageId, setIsGettingPackageId] = useState(false);
    const [error, setError] = useState(null);
    const [consentDocument, setConsentDocument] = useState(null);
    const [isCheckingAccess, setIsCheckingAccess] = useState(true);
    const [showEditForm, setShowEditForm] = useState(false);
    const [editingInsuranceType, setEditingInsuranceType] = useState(null);
    const [showViewInsurance, setShowViewInsurance] = useState(false);
    const [viewingInsuranceType, setViewingInsuranceType] = useState(null);
    const [viewingInsuranceData, setViewingInsuranceData] = useState(null);
    const [secondaryInsuranceProvider, setSecondaryInsuranceProvider] = useState('medicaid');
    const [isMessageNotify, setIsMessageNotify] = useState(false);

    const [primaryInsurance, setPrimaryInsurance] = useState({
        isUploaded: false,
        isEligibilityChecked: false,
        isEligible: false,
        data: null,
        hasPackageId: false
    });
    const [secondaryInsurance, setSecondaryInsurance] = useState({
        isUploaded: false,
        isEligibilityChecked: false,
        isEligible: false,
        data: null,
        hasPackageId: false
    });

    useEffect(() => {
        const checkUserAccess = async () => {
            try {
                const response = await axios.get('/profile/general', axiosTimeoutConfig);
                const athenaResponse = await axios.get('/athena/profile', axiosTimeoutConfig);
                const profileData = response.data;

                setIsMessageNotify(profileData.messageNotify);

                if (!athenaResponse.data?.isRegistered) {
                    navigate('/profile');
                    return;
                }

                if (athenaResponse.data?.departmentId) {
                    setUserDepartment(athenaResponse.data.departmentId);
                    setSelectedDepartment(athenaResponse.data.departmentId);
                }

                await loadDepartments();

                await loadInsuranceData();

                setIsCheckingAccess(false);
            } catch (err) {
                console.error('Error checking user access:', err);
                setError({
                    type: 'error',
                    message: 'Failed to load user profile. Please try again.'
                });
                setIsCheckingAccess(false);
            }
        };

        checkUserAccess();
    }, [navigate]);

    const loadDepartments = async () => {
        try {
            const response = await axios.get('/athena/departments', axiosTimeoutConfig);
            setDepartments(response.data);
        } catch (error) {
            console.error('Error loading departments:', error);
            setError({
                type: 'error',
                message: 'Failed to load departments. Please try again.'
            });
        }
    };

    const loadInsuranceData = async () => {
        try {
            try {
                const primaryResponse = await axios.get('/athena/insurance/data?insuranceType=primary', axiosTimeoutConfig);
                setPrimaryInsurance({
                    isUploaded: true,
                    isEligibilityChecked: primaryResponse.data.isEligibilityChecked,
                    isEligible: primaryResponse.data.isEligible,
                    data: primaryResponse.data.data,
                    hasPackageId: primaryResponse.data.hasPackageId
                });
            } catch (primaryError) {
                if (primaryError.response?.status !== 404) {
                    console.error('Error loading primary insurance:', primaryError);
                }
            }

            try {
                const secondaryResponse = await axios.get('/athena/insurance/data?insuranceType=secondary', axiosTimeoutConfig);
                setSecondaryInsurance({
                    isUploaded: true,
                    isEligibilityChecked: secondaryResponse.data.isEligibilityChecked,
                    isEligible: secondaryResponse.data.isEligible,
                    data: secondaryResponse.data.data,
                    hasPackageId: secondaryResponse.data.hasPackageId
                });
            } catch (secondaryError) {
                if (secondaryError.response?.status !== 404) {
                    console.error('Error loading secondary insurance:', secondaryError);
                }
            }
        } catch (error) {
            console.error('Error loading insurance data:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleConsentUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setConsentDocument(file);
        }
    };

    const handleConsentSubmit = async () => {
        if (!consentDocument || !formData.consentGiven) {
            setError({
                type: 'error',
                message: 'Please provide consent and upload a consent document.'
            });
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('consentDocument', consentDocument);
            formDataToSend.append('consentGiven', formData.consentGiven);
            formDataToSend.append('departmentId', selectedDepartment);

            const response = await axios.post('/athena/customer/consent', formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                ...axiosTimeoutConfig
            });


            setConsentDocument(null);
            setFormData(prev => ({
                ...prev,
                consentGiven: false
            }));

            setError({
                type: 'success',
                message: 'Consent form uploaded successfully!'
            });

        } catch (error) {
            console.error('Error uploading consent document:', error);
            setError({
                type: 'error',
                message: error.response?.data?.message || 'Failed to upload consent document'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getInsurancePackageId = async (insuranceType, insuranceProvider = 'medicare') => {
        setIsGettingPackageId(true);
        setError(null);

        try {
            const response = await axios.post('/athena/insurance/package', {
                insuranceType: insuranceType,
                insuranceProvider: insuranceProvider
            }, axiosTimeoutConfig);


            if (insuranceType === 'primary') {
                setPrimaryInsurance(prev => ({
                    ...prev,
                    hasPackageId: true
                }));
            } else {
                setSecondaryInsurance(prev => ({
                    ...prev,
                    hasPackageId: true
                }));
            }

            return response.data.insurancePackageId;
        } catch (error) {
            console.error(`Error getting ${insuranceType} insurance package ID:`, error);
            setError({
                type: 'error',
                message: error.response?.data?.message || `Failed to get ${insuranceType} insurance package ID`
            });
            return null;
        } finally {
            setIsGettingPackageId(false);
        }
    };

    const uploadInsuranceData = async (insuranceType) => {
        setIsInsuranceUploading(true);
        setError(null);

        try {
            let packageId = null;
            const insuranceData = insuranceType === 'primary' ? primaryInsurance : secondaryInsurance;

            if (!insuranceData.hasPackageId) {
                const provider = insuranceType === 'primary' ? 'medicare' : secondaryInsuranceProvider;
                packageId = await getInsurancePackageId(insuranceType, provider);
                if (!packageId) {
                    throw new Error(`Failed to get ${insuranceType} insurance package ID`);
                }
            }

            const insuranceDataToSend = {
                sequenceNumber: formData.sequenceNumber,
                insuranceIdNumber: formData.insuranceIdNumber,
                insurancePolicyHolderSex: formData.gender,
                insuranceType: insuranceType,
                departmentId: selectedDepartment
            };

            const insuranceResponse = await axios.post('/athena/insurance/upload', insuranceDataToSend, {
                headers: {
                    'Content-Type': 'application/json',
                },
                ...axiosTimeoutConfig
            });

            const updatedData = {
                sequenceNumber: formData.sequenceNumber,
                insuranceIdNumber: formData.insuranceIdNumber,
                insurancePolicyHolderSex: formData.gender,
                departmentId: selectedDepartment
            };

            if (insuranceType === 'primary') {
                setPrimaryInsurance(prev => ({
                    ...prev,
                    isUploaded: true,
                    isEligibilityChecked: false,
                    isEligible: false,
                    data: updatedData,
                    hasPackageId: true
                }));
            } else {
                setSecondaryInsurance(prev => ({
                    ...prev,
                    isUploaded: true,
                    isEligibilityChecked: false,
                    isEligible: false,
                    data: updatedData,
                    hasPackageId: true
                }));
            }

            setShowEditForm(false);
            setEditingInsuranceType(null);

            setFormData({
                gender: '',
                insuranceIdNumber: '',
                sequenceNumber: '',
                consentGiven: false,
            });

        } catch (insuranceError) {
            console.error(`Error uploading ${insuranceType} insurance data:`, insuranceError);
            setError({
                type: 'error',
                message: insuranceError.response?.data?.message || insuranceError.message || `Failed to upload ${insuranceType} insurance data`
            });
        } finally {
            setIsInsuranceUploading(false);
        }
    };

    const checkEligibility = async (insuranceType) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.post('/athena/customer/eligibility', {
                insuranceType: insuranceType
            }, axiosTimeoutConfig);

            if (insuranceType === 'primary') {
                setPrimaryInsurance(prev => ({
                    ...prev,
                    isEligibilityChecked: true,
                    isEligible: response.data.isEligible
                }));
            } else {
                setSecondaryInsurance(prev => ({
                    ...prev,
                    isEligibilityChecked: true,
                    isEligible: response.data.isEligible
                }));
            }

        } catch (err) {
            setError({
                type: 'error',
                message: err.response?.data?.message || 'An error occurred while checking eligibility'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditData = (insuranceType) => {
        setEditingInsuranceType(insuranceType);
        setShowEditForm(true);
        setError(null);

        const insuranceData = insuranceType === 'primary' ? primaryInsurance : secondaryInsurance;
        if (insuranceData.data) {
            setFormData({
                gender: insuranceData.data.insurancePolicyHolderSex || '',
                insuranceIdNumber: insuranceData.data.insuranceIdNumber || '',
                sequenceNumber: insuranceData.data.sequenceNumber || '',
                consentGiven: false,
            });
        }
    };

    const handleViewInsurance = async (insuranceType) => {
        try {
            const response = await axios.get(`/athena/insurance/data?insuranceType=${insuranceType}`, axiosTimeoutConfig);
            setViewingInsuranceData(response.data);
            setViewingInsuranceType(insuranceType);
            setShowViewInsurance(true);
        } catch (error) {
            console.error(`Error loading ${insuranceType} insurance data:`, error);
            setError({
                type: 'error',
                message: `Failed to load ${insuranceType} insurance data`
            });
        }
    };

    if (isCheckingAccess) {
        return (
            <div className="min-h-screen mb-12 bg-gradient-to-b from-sky-50 via-white to-sky-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen mb-12 bg-gradient-to-b from-sky-50 via-white to-sky-50">
            {/* Header Section */}
            <div className="mx-auto pt-8" style={{ maxWidth: '1100px' }}>
                <div className="sm:flex sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                            Medicare Eligibility Check
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">
                            Verify your eligibility for Medicare-covered home therapy services
                        </p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="mt-8">
                    <div className="bg-white rounded-2xl shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 p-8">
                        {/* Requirements Information */}
                        <div className="mb-12 space-y-6">
                            <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-xl p-8 shadow-sm">
                                <h2 className="text-xl font-semibold text-blue-900 mb-6 flex items-center">
                                    <ShieldCheckIcon className="h-6 w-6 mr-2 text-blue-500" />
                                    Eligibility Requirements
                                </h2>
                                <div className="space-y-4">
                                    <div className="flex items-start group hover:bg-white/50 p-3 rounded-lg transition-all duration-200">
                                        <div className="flex-shrink-0">
                                            <CheckCircleIcon className="h-6 w-6 text-blue-500 mt-0.5 group-hover:scale-110 transition-transform duration-200" />
                                        </div>
                                        <p className="ml-3 text-base text-blue-700 group-hover:text-blue-900 transition-colors duration-200">
                                            Be enrolled in Medicare
                                        </p>
                                    </div>
                                    <div className="flex items-start group hover:bg-white/50 p-3 rounded-lg transition-all duration-200">
                                        <div className="flex-shrink-0">
                                            <CheckCircleIcon className="h-6 w-6 text-blue-500 mt-0.5 group-hover:scale-110 transition-transform duration-200" />
                                        </div>
                                        <p className="ml-3 text-base text-blue-700 group-hover:text-blue-900 transition-colors duration-200">
                                            Live in our service area
                                        </p>
                                    </div>
                                    <div className="flex items-start group hover:bg-white/50 p-3 rounded-lg transition-all duration-200">
                                        <div className="flex-shrink-0">
                                            <CheckCircleIcon className="h-6 w-6 text-blue-500 mt-0.5 group-hover:scale-110 transition-transform duration-200" />
                                        </div>
                                        <p className="ml-3 text-base text-blue-700 group-hover:text-blue-900 transition-colors duration-200">
                                            Consent to therapy visits in your home
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <InformationCircleIcon className="h-6 w-6 text-sky-500" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-sky-900">Coverage Verification</h3>
                                            <p className="mt-2 text-sm text-sky-700">
                                                We will help verify your coverage before scheduling.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <ExclamationCircleIcon className="h-6 w-6 text-amber-500" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-amber-900">Important Note</h3>
                                            <p className="mt-2 text-sm text-amber-700">
                                                Some Medicare Advantage plans may have different coverage rules.
                                                Please review your benefits carefully.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* SMS Notifications Reminder */}
                            {!isMessageNotify && (
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0">
                                            <svg className="h-6 w-6 text-green-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <div className="ml-3 flex-1">
                                            <h3 className="text-sm font-medium text-green-900">Stay Updated with SMS Notifications</h3>
                                            <p className="mt-2 text-sm text-green-700">
                                                Don't miss important updates about your eligibility status, appointment reminders, and service notifications.
                                                Enable SMS notifications to stay informed about your care.
                                            </p>
                                            <div className="mt-3">
                                                <button
                                                    onClick={() => navigate('/profile?tab=General Information')}
                                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                                                >
                                                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    Manage Notifications
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Insurance Management Section */}
                        <div className="mb-8">
                            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <ShieldCheckIcon className="h-6 w-6 mr-2 text-gray-500" />
                                Insurance Management
                            </h2>

                            {/* Primary Insurance */}
                            <div className="mb-6 p-6 bg-gray-50 rounded-xl">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">Primary Insurance</h3>
                                    {primaryInsurance.isUploaded && (
                                        <div className="flex items-center space-x-2">
                                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                            <span className="text-sm text-green-700">Uploaded</span>
                                        </div>
                                    )}
                                </div>

                                {!primaryInsurance.isUploaded ? (
                                    <button
                                        onClick={() => {
                                            setEditingInsuranceType('primary');
                                            setShowEditForm(true);
                                            setFormData({
                                                gender: '',
                                                insuranceIdNumber: '',
                                                sequenceNumber: '',
                                                consentGiven: false,
                                            });
                                        }}
                                        disabled={isGettingPackageId}
                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                    >
                                        {isGettingPackageId ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Getting Medicare Package...
                                            </>
                                        ) : (
                                            <>
                                                <PlusIcon className="h-4 w-4 mr-2" />
                                                Upload Primary Insurance to Athena
                                            </>
                                        )}
                                    </button>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                onClick={() => handleViewInsurance('primary')}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                            >
                                                <EyeIcon className="h-4 w-4 mr-2" />
                                                View Primary Insurance
                                            </button>
                                            <button
                                                onClick={() => handleEditData('primary')}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                            >
                                                <PencilIcon className="h-4 w-4 mr-2" />
                                                Change Primary Insurance
                                            </button>
                                            <button
                                                onClick={() => checkEligibility('primary')}
                                                disabled={isLoading}
                                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Checking...
                                                    </>
                                                ) : (
                                                    <>
                                                        <ClipboardDocumentCheckIcon className="h-4 w-4 mr-2" />
                                                        Check Primary Eligibility
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {/* Primary Insurance Status */}
                                        {primaryInsurance.isEligibilityChecked && (
                                            <div className={`rounded-lg p-4 ${primaryInsurance.isEligible ? 'bg-green-50' : 'bg-amber-50'
                                                }`}>
                                                <div className="flex items-center">
                                                    {primaryInsurance.isEligible ? (
                                                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                                                    ) : (
                                                        <ExclamationCircleIcon className="h-5 w-5 text-amber-500 mr-2" />
                                                    )}
                                                    <span className={`text-sm font-medium ${primaryInsurance.isEligible ? 'text-green-800' : 'text-amber-800'
                                                        }`}>
                                                        {primaryInsurance.isEligible
                                                            ? 'Eligible for Medicare-covered services'
                                                            : 'Not eligible for Medicare-covered services'
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Secondary Insurance */}
                            <div className="p-6 bg-gray-50 rounded-xl">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">Secondary Insurance</h3>
                                    {secondaryInsurance.isUploaded && (
                                        <div className="flex items-center space-x-2">
                                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                            <span className="text-sm text-green-700">Uploaded</span>
                                        </div>
                                    )}
                                </div>

                                {!secondaryInsurance.isUploaded ? (
                                    <div className="space-y-3">
                                        <p className="text-sm text-gray-600">
                                            Secondary insurance is optional and can help cover additional costs.
                                        </p>

                                        {/* Provider Selection */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Insurance Provider
                                            </label>
                                            <select
                                                value={secondaryInsuranceProvider}
                                                onChange={(e) => setSecondaryInsuranceProvider(e.target.value)}
                                                className="block w-full px-3 py-2 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm transition-colors duration-200"
                                            >
                                                <option value="medicaid">Medicaid</option>
                                                <option value="medigap">Medigap</option>
                                            </select>
                                        </div>

                                        <button
                                            onClick={() => {
                                                setEditingInsuranceType('secondary');
                                                setShowEditForm(true);
                                                setFormData({
                                                    gender: '',
                                                    insuranceIdNumber: '',
                                                    sequenceNumber: '',
                                                    consentGiven: false,
                                                });
                                            }}
                                            disabled={isGettingPackageId}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                        >
                                            {isGettingPackageId ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Getting {secondaryInsuranceProvider === 'medicaid' ? 'Medicaid' : 'Medigap'} Package...
                                                </>
                                            ) : (
                                                <>
                                                    <PlusIcon className="h-4 w-4 mr-2" />
                                                    Upload Secondary Insurance to Athena
                                                </>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                onClick={() => handleViewInsurance('secondary')}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                            >
                                                <EyeIcon className="h-4 w-4 mr-2" />
                                                View Secondary Insurance
                                            </button>
                                            <button
                                                onClick={() => handleEditData('secondary')}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                            >
                                                <PencilIcon className="h-4 w-4 mr-2" />
                                                Change Secondary Insurance
                                            </button>
                                            <button
                                                onClick={() => checkEligibility('secondary')}
                                                disabled={isLoading}
                                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Checking...
                                                    </>
                                                ) : (
                                                    <>
                                                        <ClipboardDocumentCheckIcon className="h-4 w-4 mr-2" />
                                                        Check Secondary Eligibility
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {/* Secondary Insurance Status */}
                                        {secondaryInsurance.isEligibilityChecked && (
                                            <div className={`rounded-lg p-4 ${secondaryInsurance.isEligible ? 'bg-green-50' : 'bg-amber-50'
                                                }`}>
                                                <div className="flex items-center">
                                                    {secondaryInsurance.isEligible ? (
                                                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                                                    ) : (
                                                        <ExclamationCircleIcon className="h-5 w-5 text-amber-500 mr-2" />
                                                    )}
                                                    <span className={`text-sm font-medium ${secondaryInsurance.isEligible ? 'text-green-800' : 'text-amber-800'
                                                        }`}>
                                                        {secondaryInsurance.isEligible
                                                            ? 'Eligible for Medicare-covered services'
                                                            : 'Not eligible for Medicare-covered services'
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Form - Show when editing */}
                        {showEditForm && (
                            <form onSubmit={(e) => { e.preventDefault(); uploadInsuranceData(editingInsuranceType); }} className="space-y-8">
                                <div className="bg-blue-50 rounded-xl p-6">
                                    <h2 className="text-xl font-semibold text-blue-900 mb-4">
                                        {editingInsuranceType === 'primary' ? 'Primary' : 'Secondary'} Insurance Information
                                    </h2>
                                    <p className="text-blue-700 mb-6">
                                        Please provide your {editingInsuranceType === 'primary' ? 'primary' : 'secondary'} insurance details.
                                    </p>
                                </div>

                                {/* Department Selection */}
                                <div className="bg-gray-50 rounded-xl p-6">
                                    <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                        <BuildingOfficeIcon className="h-6 w-6 mr-2 text-gray-500" />
                                        Department Selection
                                    </h2>
                                    <div className="space-y-2">
                                        <label htmlFor="departmentId" className="block text-sm font-medium text-gray-700">
                                            Department *
                                        </label>
                                        <div className="space-y-3">
                                            <div className="flex items-center space-x-2">
                                                <div className="text-sm text-gray-600">
                                                    Your default department: <span className="font-medium text-gray-900">{departments.find(d => d.id === userDepartment)?.name || 'Unknown'}</span>
                                                </div>
                                            </div>
                                            <select
                                                name="departmentId"
                                                id="departmentId"
                                                required
                                                value={selectedDepartment || userDepartment || ''}
                                                onChange={(e) => setSelectedDepartment(e.target.value)}
                                                className="block w-full h-10 px-3 py-2 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm transition-colors duration-200"
                                            >
                                                {departments.map(dept => (
                                                    <option
                                                        key={dept.id}
                                                        value={dept.id}
                                                    >
                                                        {dept.name} {dept.id === userDepartment ? '(Default)' : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {selectedDepartment && selectedDepartment !== userDepartment ? (
                                                <p className="text-sm text-amber-600">
                                                    Note: Using a different department may affect your eligibility check
                                                </p>
                                            ) : selectedDepartment === userDepartment && (
                                                <p className="text-sm text-green-600">
                                                    Using your default department
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Personal Information */}
                                <div className="bg-gray-50 rounded-xl p-6">
                                    <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                        <DocumentTextIcon className="h-6 w-6 mr-2 text-gray-500" />
                                        Personal Information
                                    </h2>
                                    <div className="space-y-2">
                                        <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                                            Gender *
                                        </label>
                                        <select
                                            name="gender"
                                            id="gender"
                                            required
                                            value={formData.gender}
                                            onChange={handleInputChange}
                                            className="block w-full h-10 px-3 py-2 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm transition-colors duration-200"
                                        >
                                            <option value="">Select gender</option>
                                            <option value="M">Male</option>
                                            <option value="F">Female</option>
                                            <option value="O">Other</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Insurance Information */}
                                <div className="bg-gray-50 rounded-xl p-6">
                                    <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                        <ShieldCheckIcon className="h-6 w-6 mr-2 text-gray-500" />
                                        Insurance Information
                                    </h2>
                                    <div className="space-y-6">
                                        <div className="space-y-2">
                                            <label htmlFor="insuranceIdNumber" className="block text-sm font-medium text-gray-700">
                                                Medicare ID Number (MBI) *
                                            </label>
                                            <input
                                                type="text"
                                                name="insuranceIdNumber"
                                                id="insuranceIdNumber"
                                                required
                                                value={formData.insuranceIdNumber}
                                                onChange={handleInputChange}
                                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm transition-colors duration-200"
                                                placeholder="Enter your Medicare ID number (MBI)"
                                            />
                                            <p className="text-xs text-gray-500">
                                                Your Medicare Beneficiary Identifier (MBI) is an 11-character code found on your red, white, and blue Medicare card.
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="sequenceNumber" className="block text-sm font-medium text-gray-700">
                                                Sequence Number
                                            </label>
                                            <input
                                                type="text"
                                                name="sequenceNumber"
                                                id="sequenceNumber"
                                                value={formData.sequenceNumber}
                                                onChange={handleInputChange}
                                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm transition-colors duration-200"
                                                placeholder="Enter sequence number (if applicable)"
                                            />
                                            <p className="text-xs text-gray-500">
                                                This is typically found on your Medicare card and indicates the order of coverage.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="flex justify-end space-x-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowEditForm(false);
                                            setEditingInsuranceType(null);
                                            setFormData({
                                                gender: '',
                                                insuranceIdNumber: '',
                                                sequenceNumber: '',
                                                consentGiven: false,
                                            });
                                        }}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isInsuranceUploading || isGettingPackageId || !formData.gender || !formData.insuranceIdNumber || !formData.sequenceNumber}
                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                    >
                                        {isGettingPackageId ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Getting Package ID...
                                            </>
                                        ) : isInsuranceUploading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <ShieldCheckIcon className="h-4 w-4 mr-2" />
                                                Upload {editingInsuranceType === 'primary' ? 'Primary' : 'Secondary'} Insurance
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* View Insurance Modal */}
                        {showViewInsurance && viewingInsuranceData && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                                    <div className="mt-3">
                                        <div className="flex items-center justify-between mb-6">
                                            <h3 className="text-xl font-semibold text-gray-900">
                                                {viewingInsuranceType === 'primary' ? 'Primary' : 'Secondary'} Insurance Details
                                            </h3>
                                            <button
                                                onClick={() => {
                                                    setShowViewInsurance(false);
                                                    setViewingInsuranceData(null);
                                                    setViewingInsuranceType(null);
                                                }}
                                                className="text-gray-400 hover:text-gray-600"
                                            >
                                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        {/* Insurance Plan Information */}
                                        <div className="bg-blue-50 rounded-lg p-4 mb-6">
                                            <h4 className="text-lg font-medium text-blue-900 mb-3">Insurance Plan</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-blue-700">Plan Name</label>
                                                    <p className="mt-1 text-sm text-blue-900 font-medium">{viewingInsuranceData.data.insuranceplanname || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-blue-700">Display Name</label>
                                                    <p className="mt-1 text-sm text-blue-900">{viewingInsuranceData.data.insuranceplandisplayname || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-blue-700">Insurance Type</label>
                                                    <p className="mt-1 text-sm text-blue-900">{viewingInsuranceData.data.insurancetype || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-blue-700">Product Type</label>
                                                    <p className="mt-1 text-sm text-blue-900">{viewingInsuranceData.data.insuranceproducttype || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Policy Holder Information */}
                                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                            <h4 className="text-lg font-medium text-gray-900 mb-3">Policy Holder Information</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                                                    <p className="mt-1 text-sm text-gray-900 font-medium">{viewingInsuranceData.data.insurancepolicyholder || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                                                    <p className="mt-1 text-sm text-gray-900">{viewingInsuranceData.data.insurancepolicyholdersex || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                                                    <p className="mt-1 text-sm text-gray-900">{viewingInsuranceData.data.insurancepolicyholderdob || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Relationship to Insured</label>
                                                    <p className="mt-1 text-sm text-gray-900">{viewingInsuranceData.data.relationshiptoinsured || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Insurance Details */}
                                        <div className="bg-green-50 rounded-lg p-4 mb-6">
                                            <h4 className="text-lg font-medium text-green-900 mb-3">Insurance Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-green-700">Insurance ID Number (MBI)</label>
                                                    <p className="mt-1 text-sm text-green-900 font-mono font-medium">{viewingInsuranceData.data.insuranceidnumber || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-green-700">Sequence Number</label>
                                                    <p className="mt-1 text-sm text-green-900">{viewingInsuranceData.data.sequencenumber || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-green-700">Insurance ID</label>
                                                    <p className="mt-1 text-sm text-green-900">{viewingInsuranceData.data.insuranceid || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-green-700">Package ID</label>
                                                    <p className="mt-1 text-sm text-green-900">{viewingInsuranceData.data.insurancepackageid || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Eligibility Information */}
                                        <div className="bg-amber-50 rounded-lg p-4 mb-6">
                                            <h4 className="text-lg font-medium text-amber-900 mb-3">Eligibility Information</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-amber-700">Eligibility Status</label>
                                                    <div className={`mt-1 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${viewingInsuranceData.data.eligibilitystatus === 'ELIGIBLE'
                                                        ? 'bg-green-100 text-green-800'
                                                        : viewingInsuranceData.data.eligibilitystatus === 'Unverified'
                                                            ? 'bg-yellow-100 text-yellow-800'
                                                            : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {viewingInsuranceData.data.eligibilitystatus || 'Unknown'}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-amber-700">Last Checked</label>
                                                    <p className="mt-1 text-sm text-amber-900">{viewingInsuranceData.data.eligibilitylastchecked || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-amber-700">Eligibility Reason</label>
                                                    <p className="mt-1 text-sm text-amber-900">{viewingInsuranceData.data.eligibilityreason || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-amber-700">Last Updated</label>
                                                    <p className="mt-1 text-sm text-amber-900">{viewingInsuranceData.data.lastupdated || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Contact Information */}
                                        <div className="bg-purple-50 rounded-lg p-4 mb-6">
                                            <h4 className="text-lg font-medium text-purple-900 mb-3">Contact Information</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-purple-700">Insurance Phone</label>
                                                    <p className="mt-1 text-sm text-purple-900">{viewingInsuranceData.data.insurancephone || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-purple-700">Payer Name</label>
                                                    <p className="mt-1 text-sm text-purple-900">{viewingInsuranceData.data.insurancepayername || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-purple-700">Address</label>
                                                    <p className="mt-1 text-sm text-purple-900">
                                                        {viewingInsuranceData.data.insurancepackageaddress1 || 'N/A'}
                                                        {viewingInsuranceData.data.insurancepackagecity && `, ${viewingInsuranceData.data.insurancepackagecity}`}
                                                        {viewingInsuranceData.data.insurancepackagestate && `, ${viewingInsuranceData.data.insurancepackagestate}`}
                                                        {viewingInsuranceData.data.insurancepackagezip && ` ${viewingInsuranceData.data.insurancepackagezip}`}
                                                    </p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-purple-700">Country</label>
                                                    <p className="mt-1 text-sm text-purple-900">{viewingInsuranceData.data.insurancepolicyholdercountrycode || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* System Information */}
                                        <div className="bg-gray-100 rounded-lg p-4 mb-6">
                                            <h4 className="text-lg font-medium text-gray-900 mb-3">System Information</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Created Date</label>
                                                    <p className="mt-1 text-sm text-gray-900">{viewingInsuranceData.data.created || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Created By</label>
                                                    <p className="mt-1 text-sm text-gray-900">{viewingInsuranceData.data.createdby || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Last Updated By</label>
                                                    <p className="mt-1 text-sm text-gray-900">{viewingInsuranceData.data.lastupdatedby || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Confidentiality Code</label>
                                                    <p className="mt-1 text-sm text-gray-900">{viewingInsuranceData.data.confidentialitycode || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-6 flex justify-end">
                                            <button
                                                onClick={() => {
                                                    setShowViewInsurance(false);
                                                    setViewingInsuranceData(null);
                                                    setViewingInsuranceType(null);
                                                }}
                                                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Error/Success Message */}
                        {error && (
                            <div className={`rounded-xl p-6 mt-5 ${error.type === 'error' ? 'bg-red-50' : 'bg-green-50'
                                }`}>
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        {error.type === 'error' ? (
                                            <ExclamationCircleIcon className="h-6 w-6 text-red-400" />
                                        ) : (
                                            <CheckCircleIcon className="h-6 w-6 text-green-400" />
                                        )}
                                    </div>
                                    <div className="ml-3">
                                        <h3 className={`text-sm font-medium ${error.type === 'error' ? 'text-red-800' : 'text-green-800'
                                            }`}>
                                            {error.type === 'error' ? 'Error' : 'Success'}
                                        </h3>
                                        <div className={`mt-2 text-sm ${error.type === 'error' ? 'text-red-700' : 'text-green-700'
                                            }`}>
                                            <p>{error.message}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Consent Section - Only show if user is eligible for primary insurance */}
                        {/* {(primaryInsurance.isEligible) && ( */}
                        <div className="bg-gray-50 rounded-xl p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                <ClipboardDocumentCheckIcon className="h-6 w-6 mr-2 text-gray-500" />
                                Consent for Services
                            </h2>
                            <div className="space-y-6">
                                {/* Department Selection for Consent */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Department for Services *
                                    </label>
                                    <div className="space-y-3">
                                        <div className="flex items-center space-x-2">
                                            <div className="text-sm text-gray-600">
                                                Your default department: <span className="font-medium text-gray-900">{departments.find(d => d.id === userDepartment)?.name || 'Unknown'}</span>
                                            </div>
                                        </div>
                                        <select
                                            value={selectedDepartment || userDepartment || ''}
                                            onChange={(e) => setSelectedDepartment(e.target.value)}
                                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm transition-colors duration-200 h-10 px-3 py-2"
                                        >
                                            {departments.map(dept => (
                                                <option
                                                    key={dept.id}
                                                    value={dept.id}
                                                >
                                                    {dept.name} {dept.id === userDepartment ? '(Default)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {selectedDepartment && selectedDepartment !== userDepartment ? (
                                            <p className="text-sm text-amber-600">
                                                Note: Services will be provided through a different department than your default one
                                            </p>
                                        ) : selectedDepartment === userDepartment && (
                                            <p className="text-sm text-green-600">
                                                Services will be provided through your default department
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex items-center h-5">
                                        <input
                                            id="consentGiven"
                                            name="consentGiven"
                                            type="checkbox"
                                            required
                                            checked={formData.consentGiven}
                                            onChange={handleInputChange}
                                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="consentGiven" className="font-medium text-gray-700">
                                            I consent to receive psychotherapy services in my home through the selected department
                                        </label>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="consentDocument" className="block text-sm font-medium text-gray-700">
                                        Upload Signed Consent Form (PDF)
                                    </label>
                                    <div className="mt-1 flex items-center">
                                        <input
                                            type="file"
                                            name="consentDocument"
                                            id="consentDocument"
                                            accept=".pdf"
                                            onChange={handleConsentUpload}
                                            className="block w-full text-sm text-gray-500
                                                    file:mr-4 file:py-2 file:px-4
                                                    file:rounded-lg file:border-0
                                                    file:text-sm file:font-semibold
                                                    file:bg-blue-50 file:text-blue-700
                                                    hover:file:bg-blue-100
                                                    transition-colors duration-200"
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={handleConsentSubmit}
                                    disabled={!formData.consentGiven || !consentDocument || isLoading}
                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <ClipboardDocumentCheckIcon className="h-4 w-4 mr-2" />
                                            Upload Consent Form
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                        {/* )} */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckEligibility;
